'use client';

import type React from 'react';

import { CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Check, Mail } from 'lucide-react';
import { useCallback, useState } from 'react';
import { type BillingInterval, subscriptionOptions } from '../config/subscriptions';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import type { ManageSubscriptionProps } from '../types/subscription';
import { stripe } from './stripe';
import { oneOffSubscriptions } from '../config/subscriptions';
import { useUser } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const showPacks = false;

export function ManageSubscription({ currentSubscription }: ManageSubscriptionProps) {
  const { apiCall } = useAuthenticatedApi();
  const { toast } = useToast();
  const { user } = useUser();

  // State for the email confirmation dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);

  // Reset subscription status if ending or cancelled
  if (currentSubscription === 'ending' || currentSubscription === 'cancelled') {
    currentSubscription = '';
  }

  // Set billing interval to monthly since we're hiding the quarterly options
  const billingInterval: BillingInterval = 'monthly';

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setBillingEmail(email);
    setIsEmailValid(validateEmail(email));
  };

  const openSubscriptionDialog = (subscription: string) => {
    // Set the default email to the user's primary email
    const defaultEmail = user?.primaryEmailAddress?.emailAddress || '';
    setBillingEmail(defaultEmail);
    setIsEmailValid(true);
    setSelectedSubscription(subscription);
    setIsDialogOpen(true);
  };

  const handleSubscribe = useCallback(
    async (subscription: string, email: string) => {
      if (!stripe) {
        console.error('Stripe has not been initialized');
        toast({
          title: 'Error',
          description: 'Unable to process payment. Please try again later.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const response = await apiCall<{ id: string }>('/create-checkout-session', 'POST', {
          subscription,
          billingEmail: email,
        });
        if (response === undefined) return;

        const { error } = await stripe.redirectToCheckout({
          sessionId: response.id,
        });
        if (error) {
          console.error('Stripe checkout error:', error);
          toast({
            title: 'Error',
            description: 'Unable to process checkout. Please try again.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error creating checkout session:', error);
        toast({
          title: 'Error',
          description: 'Unable to start checkout process. Please try again.',
          variant: 'destructive',
        });
      }
    },
    [apiCall, toast]
  );

  const handleConfirmEmail = () => {
    if (!isEmailValid) return;

    handleSubscribe(selectedSubscription, billingEmail);
    setIsDialogOpen(false);
  };

  return (
    <>
      <div className="space-y-10">
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold">Monthly Subscriptions</h2>
            <p className="text-muted-foreground">Choose a plan that works for your needs</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {subscriptionOptions[billingInterval].map((option) => (
              <Card
                key={option.subscription}
                className={cn(
                  'relative flex flex-col transition-all hover:shadow-lg',
                  currentSubscription &&
                    (currentSubscription === option.subscription ? 'ring-2 ring-primary' : 'opacity-50'),
                  option.bestValue && 'border-primary shadow-md'
                )}
              >
                {option.bestValue && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2" variant="default">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="flex flex-col items-center justify-center">
                    <div className="text-4xl font-bold">
                      {option.price}
                      <span className="text-lg text-muted-foreground">/month</span>
                    </div>
                    <p className="mt-1 text-lg font-normal text-muted-foreground">{option.credits} credits</p>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-2 text-sm">
                    {option.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-primary" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  {currentSubscription ? (
                    currentSubscription === option.subscription ? (
                      <Button disabled className="w-full">
                        Current Plan
                      </Button>
                    ) : null
                  ) : (
                    <Button
                      onClick={() => openSubscriptionDialog(option.subscription)}
                      className="w-full"
                      variant={option.bestValue ? 'default' : 'outline'}
                    >
                      Subscribe
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        {showPacks && (
          <>
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-4 text-sm text-muted-foreground">OR</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-bold">One-off Credit Packages</h2>
                <p className="text-muted-foreground">Need a quick top-up? Purchase credits without a subscription</p>
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                {oneOffSubscriptions.map((option) => (
                  <Card
                    key={option.subscription}
                    className={cn(
                      'relative flex flex-col transition-all hover:shadow-lg',
                      option.bestValue && 'border-primary shadow-md'
                    )}
                  >
                    {option.bestValue && (
                      <Badge className="absolute -top-2 left-1/2 -translate-x-1/2" variant="default">
                        Best Value
                      </Badge>
                    )}
                    <CardHeader>
                      <CardTitle className="flex flex-col items-center justify-center">
                        <div className="text-4xl font-bold">{option.price}</div>
                        <p className="mt-1 text-lg font-normal text-muted-foreground">{option.credits} credits</p>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <ul className="space-y-2 text-sm">
                        {option.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-center">
                            <Check className="mr-2 h-4 w-4 text-primary" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        onClick={() => openSubscriptionDialog(option.subscription)}
                        className="w-full"
                        variant={option.bestValue ? 'default' : 'outline'}
                      >
                        Purchase
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Cancellation Information */}
        {currentSubscription && (
          <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium">Need to cancel your subscription?</h3>
            </div>
            <p className="mt-2 text-muted-foreground">
              To cancel your active subscription, please email us at{' '}
              <a href="mailto:support@creativeaiwriter.com" className="font-medium text-primary hover:underline">
                support@creativeaiwriter.com
              </a>
              . We'll process your request within 1-2 business days.
            </p>
          </div>
        )}
      </div>

      {/* Email Confirmation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Billing Email</DialogTitle>
            <DialogDescription>
              Please confirm or update the email address that will be used for billing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="billing-email" className="col-span-4">
                Billing Email
              </Label>
              <Input
                id="billing-email"
                value={billingEmail}
                onChange={handleEmailChange}
                className={cn('col-span-4', !isEmailValid && 'border-red-500 focus-visible:ring-red-500')}
                placeholder="your-email@example.com"
              />
              {!isEmailValid && <p className="col-span-4 text-sm text-red-500">Please enter a valid email address</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmEmail} disabled={!isEmailValid}>
              Continue to Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
